import {
  createContext,
  useCallback,
  useMemo,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { authApi } from '@/api/endpoints';
import { tokenStore, workspaceStore } from '@/auth/stores';
import { AppSplash } from '@/components/common/AppSplash';
import type { AuthSession, AuthUser, WorkspaceSummary } from '@/types/api';

type AuthStatus = 'resolving' | 'authenticated' | 'anonymous';

interface AuthContextValue {
  status: AuthStatus;
  user: AuthUser | null;
  workspaces: WorkspaceSummary[];
  /** Adopt a session returned by login or register. */
  signIn: (session: AuthSession) => void;
  signOut: () => void;
  refresh: () => Promise<unknown>;
}

// eslint-disable-next-line react-refresh/only-export-components
export const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Resolves who is signed in, before anything else renders.
 *
 * The race this exists to remove: at first paint the app knows a token *string*
 * exists but not whether it is still valid. Mounting the router at that point
 * means every page fires its queries, they all 401 together, and the user sees
 * a flash of error states on the way to the login screen.
 *
 * So while `/auth/me` is in flight the provider renders a splash *instead of*
 * the router. No route element ever mounts in an unknown auth state, which
 * means no guard has to guess and no query runs against a dead token.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  const token = useSyncExternalStore(tokenStore.subscribe, tokenStore.get);

  const meQuery = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: authApi.me,
    enabled: Boolean(token),
    // A rejected token fails identically on every retry, and the response
    // interceptor has already cleared it.
    retry: false,
    staleTime: Infinity,
    gcTime: Infinity,
  });

  const signIn = useCallback(
    (session: AuthSession) => {
      tokenStore.set(session.accessToken);
      if (session.activeWorkspaceId) {
        workspaceStore.set(session.activeWorkspaceId);
      }
      // Seed the cache from the login response so the app does not immediately
      // re-fetch what it was just handed.
      queryClient.setQueryData(['auth', 'me'], {
        user: session.user,
        workspaces: session.workspaces,
        activeWorkspaceId: session.activeWorkspaceId,
      });
    },
    [queryClient]
  );

  const signOut = useCallback(() => {
    tokenStore.clear();
    workspaceStore.clear();
    // Without this the next person to sign in on this browser sees a flash of
    // the previous user's blogs before the refetch lands.
    queryClient.clear();
  }, [queryClient]);

  const status: AuthStatus = !token
    ? 'anonymous'
    : meQuery.isPending
      ? 'resolving'
      : meQuery.isSuccess
        ? 'authenticated'
        : 'anonymous';

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      user: meQuery.data?.user ?? null,
      workspaces: meQuery.data?.workspaces ?? [],
      signIn,
      signOut,
      refresh: () => meQuery.refetch(),
    }),
    [status, signIn, signOut, meQuery]
  );

  if (status === 'resolving') return <AppSplash />;

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
